import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  InputAdornment,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { getPassengerCities } from "../api/client";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../constants/workspaceSegments";

const PassengerCitiesPage = () => {
  const { t } = useTranslation("products");
  const { segment } = useWorkspaceSegment();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const tmr = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(tmr);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getPassengerCities({
        page: page + 1,
        limit: rowsPerPage,
        q: debounced,
      });
      const d = raw && Array.isArray(raw.items) ? raw : raw?.data && typeof raw.data === "object" ? raw.data : {};
      setItems(Array.isArray(d?.items) ? d.items : []);
      setTotal(Number(d?.total) || 0);
    } catch (e) {
      setError(e?.message || t("passengerCitiesPage.loadError"));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debounced, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (segment !== WorkspaceSegments.PassengerTransport) {
    return <Navigate to="/products" replace />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        {t("passengerCitiesPage.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("passengerCitiesPage.subtitle")}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TextField
        fullWidth
        size="small"
        placeholder={t("passengerCitiesPage.search")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2, maxWidth: 480 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("passengerCitiesPage.colId")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colNome")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colCodigo")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colUf")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colFonte")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colStatus")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("passengerCitiesPage.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {items.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.nome}</TableCell>
                <TableCell>{row.id_interno}</TableCell>
                <TableCell>{row.uf}</TableCell>
                <TableCell>{row.fonte}</TableCell>
                <TableCell>{Number(row.status) === 1 ? t("table.statusActive") : t("table.statusInactive")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default PassengerCitiesPage;
